import { MasterView } from "./../../shared/views/master-view";
import { LegislationOverviewTabView } from "./../views/tabs/legislation-overview";
import { CategoryDocumentModel } from "./category-document";
import { LegislationDocumentModel } from "./legislation-document";
import { IDocumentRequestOptions } from "./../interfaces/document-request-options";
import { IDocumentModel } from "./../interfaces/document-model";
import {AppSettings} from "../../app-settings";
import {autoinject, Container} from "aurelia-dependency-injection";
import {prop} from "../../decorators/property";
import {DocumentHolderModel} from "../../desktop/models/document-holder";
import {CaseDocumentModel} from "../../desktop/models/case-document";
import {EUDocumentModel} from "../../desktop/models/eu-document";
import {DocumentHolderCollection} from "../../desktop/collections/document-holders";
import {SizeManager} from "../../shared/helpers/size-manager";
import {AuthorizationModel} from "../../authentication/models/authorization-model";
import {backboneModelProperties} from "../../decorators/backbone-properties";
import {AppEvents} from "../../app-events";
import {TRANSITION} from "../../desktop/views/desktop";
import {OpenDocument} from "./open-document";

@backboneModelProperties({
    relations : [
        {
            type: Backbone.Many,
            key: "DocumentHolders",
            collectionType: DocumentHolderCollection
        }
    ],
    defaults: {
        MultipleMode: false,
        DocumentHolders: []
    }
})
@autoinject
export class DesktopModel extends Backbone.AssociatedModel {

    @prop() public MultipleMode: boolean;
    @prop() public MultipleModePossible: boolean;
    @prop() public DesktopLeftPossible: boolean;
    @prop() public DesktopRightPossible: boolean;
    @prop() public SingleModePossible: boolean;
    @prop() public DocumentHolders: DocumentHolderCollection;
    @prop() public Focus: DocumentHolderModel;

    constructor(private appEvents: Backbone.Events, private sizeManager: SizeManager) {
        super();
    }

    public requestDocument(options: IDocumentRequestOptions): void {
        const holderModel = this.findOrCreateDocumentHolder(options);
        if (options.caller) {
            options.caller = this.DocumentHolders.get(options.caller);
            this.addDocumentHolderAtIndex(holderModel, this.DocumentHolders.indexOf(options.caller) + 1);
        } else {
            this.addDocumentHolder(holderModel);
        }
        this.findOrCreateDocument(options).then((documentModel) => {
            documentModel.RequestOptions = options;
            holderModel.addDocument(documentModel);
            documentModel.setUIStateOptions();
            this.appEvents.trigger(AppEvents.DesktopDocumentAdded, { documentCount: this.DocumentHolders.length });
        });
    }

    public enterDesktopFromPrecedentMap(options: IDocumentRequestOptions): void {
        const holderModel = this.findOrCreateDocumentHolder(options);
        holderModel.Compact = this.MultipleMode;
        this.addDocumentHolder(holderModel);
        this.findOrCreateDocument(options).then((documentModel) => {
            holderModel.addDocument(documentModel);
            this.appEvents.trigger(AppEvents.DesktopDocumentAdded, { documentCount: this.DocumentHolders.length });
            this.DocumentHolders.each((holder) => {
                holder.set("PrecedentMap", "exit");
            });
        });
    }

    public enterPrecedentMap(id: string): Promise<IDocumentModel> {
        return new Promise<any>((resolve) => {
            const targetModel = this.getDocumentHolderWithDocument(id);
            const docModel = this.getDocumentModelById(id);
            if (targetModel && (targetModel.PrecedentMap !== "active") && (targetModel.PrecedentMap !== "avoid")) {
                this.appEvents.off(AppEvents.DocumentReadyForPrecedentMap).on(AppEvents.DocumentReadyForPrecedentMap, () => {
                    resolve(docModel);
                });
                this.DocumentHolders.each((holder) => {
                    holder.PrecedentMap = (holder === targetModel) ? "active" : "avoid";
                  });
            } else {
                resolve();
            }
        });
    }

    public findOrCreateDocument(options: IDocumentRequestOptions): Promise<IDocumentModel> {
        return new Promise<IDocumentModel>((resolve) => {
            const existing = this.getDocumentModelById(options.id);
            if (existing) {
                resolve(existing);
            } else {
                this.createNewDocumentModel(options).then((model) => {
                    resolve(model);
                });
            }
        });
    }

    public findOrCreateDocumentHolder(options: IDocumentRequestOptions): DocumentHolderModel {
        let holderModel = this.getDocumentHolderWithDocument(options.id);
        holderModel = holderModel || new DocumentHolderModel({ InitialStageId: options.id });
        holderModel.Compact = this.MultipleMode;
        return holderModel;
    }

    public getDocumentHolderWithDocument(id: string): DocumentHolderModel {
        let foundHolder = null;
        this.DocumentHolders.each((holder: DocumentHolderModel) => {
            const document = holder.getPotentialDocumentById(id);
            if (document !== null) {
                foundHolder = holder;
                return false;
            }
            return true;
        });
        return foundHolder;
    }

    public getDocumentModelById(id: string): IDocumentModel {
        let foundDocument = null;
        this.DocumentHolders.each((holder: DocumentHolderModel) => {
            const document = holder.getDocumentById(id);
            if (document !== null) {
                foundDocument = document;
                return false;
            }
            return true;
        });
        return foundDocument;
    }

    public get FocusedIndex(): number {
         return this.DocumentHolders.indexOf(this.Focus);
    }

    public addDocumentHolder(holder: DocumentHolderModel, setAsFocus = true) {
        this.addDocumentHolderAtIndex(holder, this.DocumentHolders.indexOf(this.Focus) + 1, setAsFocus);
    }

    public removeDocumentHolder(documentHolderToRemove: DocumentHolderModel): void {

        const wasDeletedModelFocused = this.Focus === documentHolderToRemove ? true : false; // Find this out straight away before focus is changed by view

        const documentHolders = this.DocumentHolders;
        let focusedDocHolderIndex = documentHolders.indexOf(this.Focus);

        documentHolders.remove(documentHolderToRemove); // Triggers documentHolderRemoved() in the view
        documentHolderToRemove.unset("Slot");
        // populated documents aren't retrieved from the server to save on redundent fetches, but if it's closed we want to depopulate it
        documentHolderToRemove.depopulateAllDocuments();

        if (documentHolders.length === 0) {
            Backbone.history.navigate("search", {trigger: true});

        } else { // if there's no documents left we don't want to refocus / renavigating
            if (wasDeletedModelFocused) {
                if (documentHolders.length === focusedDocHolderIndex) {
                    focusedDocHolderIndex = focusedDocHolderIndex - 1; // special case for when we remove last case in list
                }
                const newFocus = documentHolders.at(focusedDocHolderIndex);
                this.Focus = newFocus;
                this.navigateToDocument(newFocus.TargetModel);
            } else {
                this.trigger("change:Focus"); // Focus is actually the same but we still want the view to redistribute the document holders
            }

        }
        documentHolderToRemove.remove();
        this.appEvents.trigger(AppEvents.DesktopDocumentRemoved, { documentCount: documentHolders.length });
    }

    public clear(): void {
        for (let i = this.DocumentHolders.length ; i >= 0 ; i--) { // using for-each causes problems because we're mutating array as we iterate
            const holder = this.DocumentHolders.at(i);
            if (!holder) {
                continue;
            }
            holder.Documents.each((document) => {
                if (!document) {
                    return;
                }
                document.remove();
            });
            holder.remove();
        }
        this.DocumentHolders.reset();

    }

    /** Enables/Disables desktop buttons eg single/multiple view and left/right scroll buttons */
    public updateButtons(): void {
        const holders = this.DocumentHolders;
        this.MultipleModePossible = holders.length >= 1;
        this.SingleModePossible =  (window.innerWidth > 1024);
        if (holders.length < 2) {
            this.DesktopLeftPossible = false;
            this.DesktopRightPossible = false;
            return;
        }
        if (this.MultipleMode) {
            const last = holders.last();
            const first = holders.first();
            this.DesktopLeftPossible = (first.Slot < 1);
            this.DesktopRightPossible = (last.Slot > this.sizeManager.DesktopSlots);
        } else {
            this.DesktopLeftPossible = (holders.indexOf(this.Focus) > 0);
            this.DesktopRightPossible = (holders.indexOf(this.Focus) < (holders.length - 1));
        }
    }

    public moveLeft(): void {
        const documentHolders = this.DocumentHolders;
        documentHolders.each((documentHolder) => {
            documentHolder.setSlot(this.MultipleMode ? documentHolder.Slot + 1 : 1, TRANSITION.ANIMATED);
        });

        let curDocHolderIndex = documentHolders.indexOf(this.Focus);
        if (this.MultipleMode) {
            // slotOffset represents an offset based on the focussed document's slot
            const slotOffset = this.sizeManager.DesktopSlots - this.Focus.Slot;
            if (curDocHolderIndex + slotOffset > 0) {
                // the offset (which may be negative) should move us to the right document to focus on
                curDocHolderIndex = curDocHolderIndex + slotOffset;
            }
        } else {
            curDocHolderIndex = curDocHolderIndex > 0 ? curDocHolderIndex - 1 : 0;
        }
        const holder = this.getDocumentHolderAtIndex(curDocHolderIndex);
        this.navigateToDocument(holder.TargetModel);
        this.updateButtons();
    }

    public moveRight(): void {
        const documentHolders = this.DocumentHolders;
        documentHolders.each((documentHolder: DocumentHolderModel) => {
            documentHolder.setSlot(this.MultipleMode ? documentHolder.Slot - 1 : 1, TRANSITION.ANIMATED);
        });

        let curDocHolderIndex = this.DocumentHolders.indexOf(this.Focus);

        if (this.MultipleMode) {
            const nMove = this.sizeManager.DesktopSlots - this.Focus.Slot;
            if (curDocHolderIndex + nMove < this.DocumentHolders.length) {
                curDocHolderIndex = curDocHolderIndex + nMove;
            }
        } else {
            if (curDocHolderIndex + 1 < documentHolders.length) {
                curDocHolderIndex = curDocHolderIndex + 1;
            }
        }
        const holder = this.getDocumentHolderAtIndex(curDocHolderIndex);
        this.navigateToDocument(holder.TargetModel);
        this.updateButtons();
    }

    public setFocusedByIndex(index: number): void {
        this.Focus = this.getDocumentHolderAtIndex(index);
    }

    public expireAllDocuments(): void {
        this.DocumentHolders.each((holder: DocumentHolderModel) => {
            holder.Documents.each((document) => {
                document.Expired = true;
            });
        });
    }

    public getDocumentList(): OpenDocument[] {
        const docList: OpenDocument[] = [];
        // Only get one document from each holder
        this.DocumentHolders.each((holder: DocumentHolderModel) => {
            const doc = holder.Documents.where({ ParentId: holder.Target })[0];
            // Don't get documents that have failed to load
            if (doc && doc.DocumentTitle) {
                let title = doc.DocumentTitle;
                if (doc.RenderType === "statute_section" && typeof (doc.get("Act").Name) !== "undefined") {
                    title = doc.get("Act").Name;
                }
                const id = doc.Id;
                docList.push({ Url: doc.Url, Title: title, Id: id });
            }
        });
        return docList;
    }

    public get TargetSlotPosition(): number {
           let targetPosition = 1;
           const documentHolders = this.DocumentHolders;
           const target = this.Focus;
           if (typeof (target) === "undefined") { return 1; }
           const idx = documentHolders.indexOf(target);
        // If single mode, or only one document, or we're the first, slot must be one;
           if (!this.MultipleMode || (documentHolders.size() === 1) || (idx === 0)) { return targetPosition; }
        // Don't move if we're already visible AND we have someone after us
           if ((target.Slot > 0) && (target.Slot < this.sizeManager.DesktopSlots) && (idx < documentHolders.size() - 1)) {
            return target.Slot;
        }
           if (idx < this.sizeManager.DesktopSlots) {
            targetPosition = (idx + 1);
        } else {
            targetPosition = this.sizeManager.DesktopSlots;
        }
           return targetPosition;
    }

    public logInfo(): void {
        if (!(console as any).table) { return; }
        const data = new Array();
        this.DocumentHolders.each((holder: DocumentHolderModel) => {
            const attributes = holder.attributes;
            attributes.DocumentCount = holder.Documents.length;
            attributes.Index = this.DocumentHolders.indexOf(holder);
            data.push(holder.id, attributes);
        });
    }

    public addDocumentHolderToEnd(holder: DocumentHolderModel, setAsFocus = true): void {
        this.addDocumentHolderAtIndex(holder, this.DocumentHolders.length, setAsFocus);
    }

    public navigateToDocument(document: IDocumentModel) {
        let url: string;
        if (document.Error) {
            url = `case/error/overview/${document.Id}`;
        } else {
            url = document.uistate.CurrentAnalysisSection ? document.Url.replace("overview", document.uistate.CurrentAnalysisSection.Identifier) : document.Url;
        }
        Backbone.history.navigate(url, true);
    }

    private addDocumentHolderAtIndex(holder: DocumentHolderModel, index: number, setAsFocus = true): void {
        this.DocumentHolders.add(holder, { at: index });
        holder.Removed = false;
        (this.MultipleMode) ? holder.contract() : holder.expand();
        if (setAsFocus) {
            this.Focus = holder;
        }
    }

    private createNewDocumentModel(options: IDocumentRequestOptions): Promise<IDocumentModel> {
        return new Promise<IDocumentModel>((resolve) => {
            return this.determineDocumentType(options).then((documentType) => {
                const model = this.createModelOfType(documentType);
                model.Id = options.id;
                model.RenderType = documentType;
                model.ParentId = options.id;
                resolve(model);
            });
        });
    }

    private determineDocumentType(options: IDocumentRequestOptions): Promise<any> {
        return new Promise<any>((resolve) => {
            if (options.type !== "document") {
                resolve(options.type);
            } else {
                this.getDocumentTypeById(options.id).then((id) => {
                    resolve(id);
                });
            }
        });
    }

    private getDocumentTypeById(id: string): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            $.ajax({
                beforeSend: AuthorizationModel.attachAuthorizationHeader,
                type: "GET",
                url: `${AppSettings.DocumentApiUrlPrefix}document/type/${id}`
            }).done((response) => {
                resolve(response);
            }).fail((error) => {
                reject(error);
            });
        });
    }

    private createModelOfType(type: JustisOneDocumentType): IDocumentModel {
        let documentModel: IDocumentModel;

        switch (type) {
            case "statute":
            case "statute_section":
            case "si":
                documentModel = new LegislationDocumentModel();
                break;
            case "case":
                documentModel = new CaseDocumentModel();
                break;
            case "eu_sector0":
            case "eu_sector1":
            case "eu_sector2":
            case "eu_sector3":
            case "eu_sector4":
            case "eu_sector5":
            case "eu_sector6":
            case "eu_sector7":
            case "eu_sector8":
            case "eu_sector9":
            case "eu_document":
            case "eu_sectorc":
                documentModel = new EUDocumentModel();
                break;
            case "category":
                documentModel = new CategoryDocumentModel();

        }
        return documentModel;
    }

    private getDocumentHolderAtIndex(index: number): DocumentHolderModel {
        return this.DocumentHolders.at(index);
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/desktop/models/desktop.ts